import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-chunk-file-displayer',
  templateUrl: './chunk-file-displayer.component.html',
  styleUrls: ['./chunk-file-displayer.component.scss'],
  standalone: true,
  imports: [MatButtonModule, MatProgressBarModule, TranslateModule, MatDialogModule, MatIconModule],
})
export class ChunkFileDisplayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fileLink: string;
  @Input() fileName: string;
  @Input() fileType: string;
  @Output() closed = new EventEmitter<void>();

  viewerType: 'pdf' | 'image' | 'unknown' = 'unknown';
  safeUrl: SafeResourceUrl | SafeUrl | null = null;
  loading = false;
  fileIsLoaded = false;

  constructor(
    private _sanitizer: DomSanitizer,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { fileLink: string; fileName: string; fileType: string },
    @Optional() private dialogRef: MatDialogRef<ChunkFileDisplayerComponent>
  ) {}

  ngOnInit(): void {
    // Fallback for Dialog usage if inputs are missing but dialogData exists
    if (!this.fileLink && this.dialogData) {
      this.fileLink = this.dialogData.fileLink;
      this.fileName = this.dialogData.fileName;
      this.fileType = this.dialogData.fileType;
    }
    this.openDocument();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileLink'] || changes['fileType']) {
      this.openDocument();
    }
  }

  ngOnDestroy(): void {}

  close() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.closed.emit();
  }

  openDocument() {
    this.loading = true;
    // Use local properties and trim them
    const fileLink = this.fileLink ? this.fileLink.trim() : '';
    const fileType = this.fileType ? this.fileType.trim() : '';

    if (!fileLink) return;

    if (this.isImage(fileType)) {
      this.viewerType = 'image';
      this.safeUrl = this._sanitizer.bypassSecurityTrustUrl(fileLink);
      this.loading = false; // Images load instantly/have their own events, but start false to show img immediately or handle with (load)
    } else if (this.isPdf(fileType)) {
      this.viewerType = 'pdf';
      this.safeUrl = this._sanitizer.bypassSecurityTrustResourceUrl(fileLink);
      this.loading = false; // Native viewer handles loading
    } else {
      this.viewerType = 'unknown';
      this.loading = false;
    }
  }

  isImage(ext: string): boolean {
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext.toLowerCase());
  }

  isPdf(ext: string): boolean {
    return ext.toLowerCase() === 'pdf';
  }

  isOffice(ext: string): boolean {
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext.toLowerCase());
  }

  onLoad() {
    this.loading = false;
  }
}
